import React, { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import HtmlSlideRenderer from './HtmlSlideRenderer';

/**
 * HtmlSlideFullscreen
 *
 * 슬라이드 풀스크린 미리보기 + 발표 모드 모달.
 * - 검은 오버레이 + 중앙 큰 16:9 슬라이드
 * - 좌우 화살표 버튼 / ←→/Space/PageUp·Down/Home/End 키로 슬라이드 이동
 * - ESC 또는 X 버튼으로 닫기 (또는 발표 모드 해제만)
 * - F 키 또는 발표 모드 버튼으로 **Fullscreen API 진입/종료** (수업용 발표)
 * - 발표 모드 진입 시:
 *   - 슬라이드만 화면 가득 (UI 자동 숨김)
 *   - 마우스 3초 비활성 시 cursor: none + UI 페이드 아웃
 *   - 하단 thin progress bar (현재 슬라이드 진행률)
 * - 모바일: 좌우 스와이프 지원
 *
 * 모든 스타일은 인라인으로 격리 (글로벌 CSS 의존 0).
 *
 * ⚠️ 중첩 모달 안전성:
 *   - 갤러리/나눔터 DetailModal 내부에서 호출되어도 안전하도록
 *     createPortal로 document.body에 직접 렌더링한다.
 *   - 그렇지 않으면 풀스크린 내부 클릭이 부모 모달 backdrop으로 버블링되어
 *     상위 모달이 닫혀버리는 버그가 발생한다.
 *
 * Props:
 *   - slides: outline.slides 배열
 *   - initialIndex: 시작 슬라이드 인덱스
 *   - onClose: 닫기 콜백
 */

const CURSOR_HIDE_DELAY_MS = 3000;
const SWIPE_THRESHOLD_PX = 50;

const HoverButton = ({ style, hoverStyle = {}, activeStyle = {}, children, onClick, onMouseDown, onMouseUp, onTouchStart, ...rest }) => {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  return (
    <button
      {...rest}
      style={{
        ...style,
        ...(hover ? hoverStyle : {}),
        ...(active ? activeStyle : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        setActive(true);
        onMouseDown?.(e);
      }}
      onMouseUp={(e) => {
        e.stopPropagation();
        setActive(false);
        onMouseUp?.(e);
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        onTouchStart?.(e);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      {children}
    </button>
  );
};

const OVERLAY_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  // 갤러리 detail 모달(z-index: 10000) 위에 떠야 하므로 더 큰 값 사용
  zIndex: 100000,
  background: 'rgba(15, 23, 42, 0.92)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4vh 8vw',
  animation: 'wqfs-fade-in 0.18s ease-out',
};

const STAGE_STYLE = {
  width: '100%',
  maxWidth: 'min(1400px, 92vw)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const ROUND_BTN_BASE = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: '#FFFFFF',
  border: 'none',
  color: '#191F28',
  cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
  transition: 'transform 0.12s ease, box-shadow 0.15s ease',
  zIndex: 2,
  padding: 0,
};

const CLOSE_BTN_STYLE = {
  ...ROUND_BTN_BASE,
  top: 24,
  right: 24,
  width: 48,
  height: 48,
};

const CLOSE_BTN_HOVER = {
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.45)',
  transform: 'scale(1.05)',
};

const CLOSE_BTN_ACTIVE = {
  transform: 'scale(0.95)',
};

const NAV_BTN_BASE = {
  ...ROUND_BTN_BASE,
  top: '50%',
  width: 56,
  height: 56,
  marginTop: -28,
};

const NAV_BTN_HOVER = {
  boxShadow: '0 6px 22px rgba(0, 0, 0, 0.5)',
  transform: 'scale(1.06)',
};

const NAV_BTN_ACTIVE = {
  transform: 'scale(0.96)',
};

const COUNTER_STYLE = {
  position: 'absolute',
  top: 28,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 16px',
  background: 'rgba(0, 0, 0, 0.45)',
  borderRadius: 999,
  color: '#FFFFFF',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.04em',
  fontFeatureSettings: '"tnum"',
  zIndex: 2,
};

const HINT_STYLE = {
  position: 'absolute',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  color: 'rgba(255, 255, 255, 0.75)',
  fontSize: 12,
  letterSpacing: '0.01em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  zIndex: 2,
};

const KBD_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 24,
  height: 22,
  padding: '0 6px',
  background: 'rgba(255, 255, 255, 0.16)',
  border: '1px solid rgba(255, 255, 255, 0.24)',
  borderRadius: 5,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: '#FFFFFF',
};

// 발표 모드 (Fullscreen API) 토글 버튼 — 닫기 옆에 배치
const PRESENT_BTN_STYLE = {
  position: 'absolute',
  top: 24,
  right: 84,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 40,
  padding: '0 14px',
  borderRadius: 20,
  background: 'rgba(255, 255, 255, 0.95)',
  color: '#191F28',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
  transition: 'transform 0.12s ease, box-shadow 0.15s ease, background 0.15s ease',
  zIndex: 2,
};

const PRESENT_BTN_HOVER = {
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.45)',
  transform: 'scale(1.03)',
};

const PRESENT_BTN_ACTIVE = {
  transform: 'scale(0.97)',
};

// 발표 모드일 때만 보이는 진행 바 (하단 얇은 막대)
const PROGRESS_BAR_WRAP_STYLE = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 3,
  background: 'rgba(255, 255, 255, 0.12)',
  zIndex: 3,
};

const PROGRESS_BAR_FILL_STYLE = {
  height: '100%',
  background: '#3182F6',
  transition: 'width 0.25s ease',
};

const HtmlSlideFullscreen = ({
  slides,
  initialIndex = 0,
  onClose,
  onRequestEdit,
  hidePresentButton = false,
  hideEditButton = false,
  closeOnBackdropClick = false,
  // Phase 1: outline.canvas_theme을 받아 HtmlSlideRenderer에 전달
  themeId = null,
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [isPresenting, setIsPresenting] = useState(false);
  // 발표 모드 시 마우스 비활성 → UI 페이드 아웃 + cursor 숨김
  const [uiVisible, setUiVisible] = useState(true);

  const overlayRef = useRef(null);
  const cursorTimerRef = useRef(null);
  const touchStartXRef = useRef(null);

  const total = Array.isArray(slides) ? slides.length : 0;
  const current = total > 0 ? slides[index] : null;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;

  // 학습지(A4 세로)는 폭에 맞추면 세로가 화면을 넘친다. 세로형이면 높이를 기준으로 잡는다.
  const pageW = current?.canvas?.width || 1280;
  const pageH = current?.canvas?.height || 720;
  const isPortraitPage = pageH > pageW;

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => (i < total - 1 ? i + 1 : i));
  }, [total]);

  const goFirst = useCallback(() => setIndex(0), []);
  const goLast = useCallback(() => setIndex((total > 0 ? total - 1 : 0)), [total]);

  // 발표 모드 진입/종료 (Fullscreen API)
  // - 진입 시: overlay element를 requestFullscreen
  // - 종료 시: document.exitFullscreen
  // - fullscreenchange 이벤트로 ESC 등 외부 트리거에도 state 동기화
  const togglePresent = useCallback(async () => {
    const elem = overlayRef.current;
    if (!elem) return;
    try {
      if (!document.fullscreenElement) {
        if (elem.requestFullscreen) await elem.requestFullscreen();
        else if (elem.webkitRequestFullscreen) await elem.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      }
    } catch (_e) {
      // 사용자 거부 또는 권한 문제 — 무시 (best-effort)
    }
  }, []);

  // fullscreenchange 동기화 (브라우저 ESC, F11 등 외부 변경 처리)
  useEffect(() => {
    const sync = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsPresenting(fs);
      if (fs) setUiVisible(true);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  // 키보드 핸들러 (Reveal.js 컨벤션 차용)
  useEffect(() => {
    const handler = (e) => {
      // 키보드 입력이 input/contenteditable에서 발생하면 무시 (텍스트 편집 보호)
      const tgt = e.target;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        // 발표 모드 중이면 발표 모드만 종료, 아니면 닫기
        if (isPresenting) {
          if (document.exitFullscreen) document.exitFullscreen();
          else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        } else {
          onClose && onClose();
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goFirst();
      } else if (e.key === 'End') {
        e.preventDefault();
        goLast();
      } else if ((e.key === 'f' || e.key === 'F') && !hidePresentButton) {
        // 발표 모드 버튼이 숨겨진 경우(라이브러리/나눔터)에는 F 키도 무시
        e.preventDefault();
        togglePresent();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goPrev, goNext, goFirst, goLast, onClose, isPresenting, togglePresent, hidePresentButton]);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // 발표 모드에서 마우스 자동 숨김 (3초 비활성)
  useEffect(() => {
    if (!isPresenting) {
      setUiVisible(true);
      if (cursorTimerRef.current) {
        clearTimeout(cursorTimerRef.current);
        cursorTimerRef.current = null;
      }
      return;
    }
    const reveal = () => {
      setUiVisible(true);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = setTimeout(() => setUiVisible(false), CURSOR_HIDE_DELAY_MS);
    };
    reveal();
    window.addEventListener('mousemove', reveal);
    window.addEventListener('mousedown', reveal);
    return () => {
      window.removeEventListener('mousemove', reveal);
      window.removeEventListener('mousedown', reveal);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    };
  }, [isPresenting]);

  // 모바일 터치 스와이프 핸들러 (좌우)
  const onTouchStart = (e) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    if (startX == null) return;
    const endX = e.changedTouches[0]?.clientX ?? startX;
    const dx = endX - startX;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX) {
      if (dx > 0) goPrev();
      else goNext();
    }
    touchStartXRef.current = null;
  };

  if (!current) return null;

  // 모바일에서 버튼 위치/크기 조정
  const closeBtnStyle = isMobile
    ? { ...CLOSE_BTN_STYLE, top: 12, right: 12, width: 40, height: 40 }
    : CLOSE_BTN_STYLE;
  const navBtnPrev = isMobile
    ? { ...NAV_BTN_BASE, left: 12, width: 44, height: 44, marginTop: -22 }
    : { ...NAV_BTN_BASE, left: 32 };
  const navBtnNext = isMobile
    ? { ...NAV_BTN_BASE, right: 12, width: 44, height: 44, marginTop: -22 }
    : { ...NAV_BTN_BASE, right: 32 };

  // createPortal로 body 직접 렌더 — 부모 모달 backdrop으로 이벤트 버블링 방지.
  // 추가 안전망: 풀스크린 내부에서 발생한 모든 클릭/마우스다운에 stopPropagation 적용.
  const stopBubble = (e) => e.stopPropagation();

  if (typeof document === 'undefined') return null;

  // 발표 모드용 동적 스타일 — fullscreen 시 padding 제거 + 진행 바 표시
  const overlayStyleDynamic = {
    ...OVERLAY_STYLE,
    // 모바일은 좌우 여백을 줄여 학습지 한 쪽이 화면 폭을 거의 다 쓰게 한다
    ...(isMobile ? { padding: '3vh 2vw' } : {}),
    ...(isPresenting ? { padding: 0, background: '#000000', backdropFilter: 'none', WebkitBackdropFilter: 'none' } : {}),
    cursor: isPresenting && !uiVisible ? 'none' : 'auto',
  };
  // 발표 모드에서 UI는 페이드 (마우스 비활성 3초)
  const uiOpacity = isPresenting && !uiVisible ? 0 : 1;
  const uiTransition = 'opacity 0.3s ease';

  const progressPct = total > 1 ? ((index + 1) / total) * 100 : 100;

  return createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={isPresenting ? '슬라이드 발표 모드' : '슬라이드 미리보기'}
      style={overlayStyleDynamic}
      onClick={(e) => {
        // 발표 모드 중에는 backdrop 클릭으로 닫지 않음 (사용자가 의도치 않게 종료되는 것 방지)
        if (isPresenting) return;
        // closeOnBackdropClick=false면 빈공간 클릭으로도 안 닫힘 (X 버튼/ESC만)
        if (!closeOnBackdropClick) return;
        if (e.target === e.currentTarget) {
          onClose && onClose();
        }
      }}
      onMouseDown={stopBubble}
      onMouseUp={stopBubble}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* 키프레임은 단일 <style> 태그로 (animation만 인라인 불가) */}
      <style>{`@keyframes wqfs-fade-in { from { opacity: 0; } to { opacity: 1; } }`}</style>

      <HoverButton
        type="button"
        aria-label="닫기"
        style={{ ...closeBtnStyle, opacity: uiOpacity, transition: uiTransition }}
        hoverStyle={CLOSE_BTN_HOVER}
        activeStyle={CLOSE_BTN_ACTIVE}
        onClick={onClose}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </HoverButton>

      {/* 편집하기 버튼 — onRequestEdit가 있고 hideEditButton이 false일 때 (라이브러리 컨텍스트에선 숨김) */}
      {!isMobile && !isPresenting && onRequestEdit && !hideEditButton && (
        <HoverButton
          type="button"
          aria-label="이 슬라이드 편집"
          style={{ ...PRESENT_BTN_STYLE, right: 200, opacity: uiOpacity, transition: uiTransition }}
          hoverStyle={PRESENT_BTN_HOVER}
          activeStyle={PRESENT_BTN_ACTIVE}
          onClick={() => onRequestEdit(index)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
          <span>편집하기</span>
        </HoverButton>
      )}

      {/* 발표 모드 토글 버튼 (모바일·hidePresentButton일 때는 숨김) */}
      {!isMobile && !hidePresentButton && (
        <HoverButton
          type="button"
          aria-label={isPresenting ? '발표 모드 종료' : '발표 모드 시작'}
          style={{ ...PRESENT_BTN_STYLE, opacity: uiOpacity, transition: uiTransition }}
          hoverStyle={PRESENT_BTN_HOVER}
          activeStyle={PRESENT_BTN_ACTIVE}
          onClick={togglePresent}
        >
          {isPresenting ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
              <span>발표 종료</span>
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="6 4 20 12 6 20 6 4" />
              </svg>
              <span>발표 시작</span>
            </>
          )}
        </HoverButton>
      )}

      <div
        style={(() => {
          if (isPresenting) {
            return { ...STAGE_STYLE, maxWidth: '100vw', width: '100vw', height: '100vh' };
          }
          if (isPortraitPage) {
            const ratio = (pageW / pageH).toFixed(4);
            return { ...STAGE_STYLE, maxWidth: `min(${isMobile ? 96 : 92}vw, calc(${isMobile ? 90 : 86}vh * ${ratio}))` };
          }
          return STAGE_STYLE;
        })()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 모든 슬라이드를 미리 마운트하고 opacity로 전환 —
            iframe 재생성/폰트 재로드 없이 즉시 넘김 (글꼴 깜빡임 0)
            ⚠️ visibility는 내부 iframe이 visibility:visible로 덮어써서
               숨김이 풀리므로 (마지막 슬라이드가 항상 위에 보이는 버그) opacity 사용 */}
        <div style={{ position: 'relative', width: '100%' }}>
          {slides.map((s, i) => (
            <div
              key={`wqfs-slide-${i}`}
              aria-hidden={i === index ? undefined : true}
              style={
                i === index
                  ? { position: 'relative', opacity: 1, zIndex: 1 }
                  : { position: 'absolute', inset: 0, opacity: 0, zIndex: 0, pointerEvents: 'none' }
              }
            >
              {s?.page_image_url ? (
                // 미리 렌더된 쪽 이미지(학습지 PNG) — HTML 렌더 없이 그대로 크게 보여준다
                <img
                  alt={`${i + 1}쪽`}
                  className="wqfs-renderer"
                  data-testid="fullscreen-page-image"
                  draggable={false}
                  src={s.page_image_url}
                  style={{ display: 'block', width: '100%', height: 'auto', background: '#FFFFFF', borderRadius: 6, userSelect: 'none' }}
                />
              ) : (
                <HtmlSlideRenderer slide={s} className="wqfs-renderer" themeId={themeId} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* HtmlSlideRenderer는 .wqslide-renderer 글로벌을 쓰지만 인라인 보조 */}
      <style>{`.wqfs-renderer { width: 100%; box-shadow: ${isPresenting ? 'none' : '0 24px 60px rgba(0, 0, 0, 0.4)'}; }`}</style>

      {index > 0 && (
        <HoverButton
          type="button"
          aria-label="이전 슬라이드"
          style={{ ...navBtnPrev, opacity: uiOpacity, transition: uiTransition }}
          hoverStyle={NAV_BTN_HOVER}
          activeStyle={NAV_BTN_ACTIVE}
          onClick={goPrev}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </HoverButton>
      )}
      {index < total - 1 && (
        <HoverButton
          type="button"
          aria-label="다음 슬라이드"
          style={{ ...navBtnNext, opacity: uiOpacity, transition: uiTransition }}
          hoverStyle={NAV_BTN_HOVER}
          activeStyle={NAV_BTN_ACTIVE}
          onClick={goNext}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </HoverButton>
      )}

      <div style={{ ...COUNTER_STYLE, opacity: uiOpacity, transition: uiTransition }} aria-live="polite">
        <span>{index + 1}</span>
        <span style={{ opacity: 0.5 }}>/</span>
        <span style={{ opacity: 0.7 }}>{total}</span>
      </div>

      {!isMobile && (
        <div style={{ ...HINT_STYLE, opacity: uiOpacity, transition: uiTransition }}>
          <kbd style={KBD_STYLE}>←</kbd>
          <kbd style={KBD_STYLE}>→</kbd>
          <span>슬라이드 이동</span>
          {!hidePresentButton && (
            <>
              <span style={{ opacity: 0.5, margin: '0 2px' }}>·</span>
              <kbd style={KBD_STYLE}>F</kbd>
              <span>발표 모드</span>
            </>
          )}
          <span style={{ opacity: 0.5, margin: '0 2px' }}>·</span>
          <kbd style={KBD_STYLE}>ESC</kbd>
          <span>{isPresenting ? '발표 종료' : '닫기'}</span>
        </div>
      )}

      {/* 발표 모드 진행 바 (하단 thin) */}
      {isPresenting && (
        <div style={PROGRESS_BAR_WRAP_STYLE} aria-hidden="true">
          <div style={{ ...PROGRESS_BAR_FILL_STYLE, width: `${progressPct}%` }} />
        </div>
      )}
    </div>,
    document.body
  );
};

export default HtmlSlideFullscreen;
